
// src/components/PosthogFeedbackTool.jsx
import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Rating,
  TextField,
  Button,
  Alert,
  Collapse,
} from "@mui/material";

function getPosthogClient() {
  if (typeof window === "undefined") return null;
  const ph = window.posthog;
  if (ph && typeof ph.capture === "function") return ph;
  return null;
}

function RatingRow({ label, value, onChange }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
        alignItems: "center",
        gap: 1,
        py: 0.5,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Rating
          value={value}
          onChange={(_, v) => onChange(v)}
          precision={1}
        />
        <Typography variant="caption" color="text.secondary">
          {value ? `${value}/5` : "—"}
        </Typography>
      </Box>
    </Box>
  );
}

export default function PosthogFeedbackTool({ meta = {} }) {
  const [videoCourse, setVideoCourse] = React.useState(null);
  const [generator, setGenerator] = React.useState(null);
  const [outcome, setOutcome] = React.useState(null);
  const [text, setText] = React.useState("");

  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState("");

  const canSubmit = Boolean(videoCourse && generator && outcome);

  const handleSubmit = async () => {
    setError("");
    setSuccess(false);

    if (!canSubmit) {
      setError("Bitte alle 3 Ratings auswählen.");
      return;
    }

    setSubmitting(true);
    try {
      const ph = getPosthogClient();

      const payload = {
        rating_videocourse: videoCourse,
        rating_generator: generator,
        rating_media_kit_outcome: outcome,
        feedback_text: (text || "").trim() || null,
        ...meta,
      };

      if (ph) {
        ph.capture("mk_feedback_submitted", payload);
      } else {
        // Fallback: still let user submit without breaking UI
        console.warn("PostHog not found on window.posthog. Feedback payload:", payload);
      }

      setSuccess(true);
      setText("");
      // Keep ratings (optional). If you want reset:
      // setVideoCourse(null); setGenerator(null); setOutcome(null);
    } catch (e) {
      console.error(e);
      setError("Konnte Feedback nicht senden. Bitte später erneut versuchen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Feedback
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hilf uns, ShowKompass zu verbessern — dauert 20 Sekunden.
            </Typography>
          </Box>

          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            <RatingRow label="Videocourse" value={videoCourse} onChange={setVideoCourse} />
            <RatingRow label="Generator" value={generator} onChange={setGenerator} />
            <RatingRow label="Media Kit Outcome" value={outcome} onChange={setOutcome} />
          </Stack>

          <TextField
            label="Feedback (optional)"
            placeholder="Was war gut? Was war nervig? Was fehlt?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />

          <Collapse in={Boolean(error)}>
            <Alert severity="error">{error}</Alert>
          </Collapse>

          <Collapse in={success}>
            <Alert severity="success">Danke! Feedback ist angekommen 🙌</Alert>
          </Collapse>

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              sx={{ fontWeight: 900 }}
            >
              {submitting ? "Sende…" : "Feedback senden"}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Mit dem Absenden stimmst du zu, dass dein Feedback zur Verbesserung des Produkts ausgewertet wird.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
